import React, { useEffect, useRef, useState } from "react";
import { Input, Card, Modal, Radio, Carousel, Form, Flex } from "antd";
import {
  ArrowLeftOutlined,
  EnvironmentFilled,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { useThemeContext } from "../../../../context/ThemeContext";
import { customLogger } from "../../../../utils/Logger";
import CustomLoader from "../../../../components/common/UI/Loader";
import CitySelectorModal from "../../../../components/common/CitySelectorModal/CitySelectorModal";
import SettingsSidebarHeader from "../../../../components/common/SettingsSidebarHeader";
import CountrySelectorModal from "../../../../components/common/CountrySelectorModal/CountrySelectorModal";
import apiInstance from "../../../../services/apiService";
import ENDPOINTS from "../../../../services/endpoint";
import StateSelectorModal from "../../../../components/common/StateSelectorModal/StateSelectorModal";
import GradientButton from "../../../../components/common/UI/GradientButton";
import "./index.scss";

interface BuyNumberProps {
  handleBackToPrevious: () => void;
  isPage?: boolean;
}

interface Country {
  code: string;
  dial: string;
  name: string;
  flag: string;
}

interface State {
  id: string;
  value: string;
}

interface City {
  id: string;
  value: string;
  npa: string;
}
interface PhoneNumberDetails {
  cost: string;
  currency: string;
  phone: string;
  type: string;
  typeName: string;
}

const BuyNumber: React.FC<BuyNumberProps> = ({
  handleBackToPrevious,
  isPage = true,
}) => {
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>({
    code: "CA",
    dial: "+1",
    name: "Canada",
    flag: "https://flagcdn.com/ca.svg",
  });
  const { theme } = useThemeContext();
  const [value, setValue] = useState<string>("area");
  const [areaCodeSearchQuery, setAreaCodeSearchQuery] = useState("");
  const [fetchedNumbers, setFetchedNumbers] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [carouselChunks, setCarouselChunks] = useState<any[]>([]);
  const carouselRef = useRef<any>(null);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [phoneNumberDetails, setPhoneNumberDetails] =
    useState<PhoneNumberDetails | null>(null);
  const [isConfirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [stateSelectorModalVisible, setStateSelectorModalVisible] =
    useState<boolean>(false);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);
  const [citySelectorModalVisible, setCitySelectorModalVisible] =
    useState<boolean>(false);
  const [paymentGatewayModalOpen, setPaymentGatewayModalOpen] = useState(false);
  const [paymentGatewayDetails, setPaymentGatewayDetails] = useState<any>(null);
  const [buyingInProgress, setBuyingInProgress] = useState(false);

  const handleChange = (e: any) => {
    setValue(e.target.value);
    setCarouselChunks([]);
    setFetchedNumbers([]);
    setSelectedCity(null);
    setSelectedState(null);
  };

  const getPhoneNumberDetails = async () => {
    const url = ENDPOINTS.GET_PHONE_DETAILS;

    try {
      const response = await apiInstance.post(url, {
        phone: selectedNumber,
      });
      const data = response.data.data;
      setPhoneNumberDetails(data[0]);
      setConfirmationModalOpen(true);
    } catch (error) {
      customLogger(error);
      toast.error("There was an error fetching selected number details");
    }
  };

  const handleBuying = () => {
    if (!selectedNumber) {
      toast.error("Please select a Number first");
      return;
    }

    getPhoneNumberDetails();
  };

  const handleAddCard = async () => {
    const url = ENDPOINTS.ADD_CARD;

    setBuyingInProgress(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.data;
      setPaymentGatewayDetails(data);
      setPaymentGatewayModalOpen(true);
    } catch (error: any) {
      customLogger(error);
      toast.error(error.message || "An error occured. Please try again.");
    } finally {
      setBuyingInProgress(false);
    }
  };

  const handleBuyingConfirmation = async () => {
    const url = ENDPOINTS.BUY_PHONE_NUMBER;
    setBuyingInProgress(true);

    try {
      const response = await apiInstance.post(url, {
        phone: String(phoneNumberDetails?.phone),
        type: phoneNumberDetails?.type,
      });

      if (response.status === 200) {
        toast.success("eFone phone number purchased/assigned successfully");
        handleBackToPrevious();
      }
    } catch (error: any) {
      // debugger
      customLogger(error);
      if (error?.response.data.hostedpageDetails) {
        toast.info("Add payment card to proceed with purchasing.");
        handleAddCard();
      } else {
        toast.error(
          error?.response?.data?.message ||
            "There was an error purchasing number. Please try again",
        );
      }
    } finally {
      setConfirmationModalOpen(false);
      setBuyingInProgress(false);
    }
  };

  const getNumbersFromNPA = async () => {
    if (!areaCodeSearchQuery) {
      toast.error("Enter Area code");
      return;
    }

    const url = ENDPOINTS.GET_NUMBERS_LIST + `?npa=${areaCodeSearchQuery}`;
    setIsLoading(true);
    try {
      const response = await apiInstance.get(url);
      const data = response.data.data;
      setFetchedNumbers(data);
    } catch (error: any) {
      customLogger(error);
      toast.error(error.message || "There was an error fetching Numbers");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedState || !selectedCity) return;

    const getNumbersByCityAndState = async () => {
      const url =
        ENDPOINTS.GET_NUMBERS_LIST +
        `?state=${selectedState.id}&city=${selectedCity.id}`;
      setIsLoading(true);

      try {
        const response = await apiInstance.get(url);
        const data = response.data.data;
        setFetchedNumbers(data);
      } catch (error) {
        customLogger(error);
        toast.error("There was an error fetching Numbers");
      } finally {
        setIsLoading(false);
      }
    };

    getNumbersByCityAndState();
  }, [selectedState, selectedCity]);

  useEffect(() => {
    const itemsPerPage = 10;
    const chunks = [];
    for (let i = 0; i < fetchedNumbers.length; i += itemsPerPage) {
      chunks.push(fetchedNumbers.slice(i, i + itemsPerPage));
    }
    setCarouselChunks(chunks);
  }, [fetchedNumbers]);

  useEffect(() => {
    setSelectedCity(null);
    setSelectedState(null);
  }, [selectedCountry]);

  useEffect(() => {
    setSelectedNumber(null);
  }, [value]);

  return (
    <>
      <div className="buy-number-container">
        <Flex align="center" gap={20}>
          <div className="icon-container" onClick={handleBackToPrevious}>
            <ArrowLeftOutlined />
          </div>

          <div>
            <h1 className="title">Buy virtual number</h1>
            <p className="subtitle">Choose your preferred location</p>
          </div>
        </Flex>

        <div className="scroll-wrapper">
          <div className="area-code">
            <Radio.Group
              disabled={isLoading}
              value={value}
              onChange={handleChange}
              className="tabs-container"
            >
              <Radio.Button
                className="tab-btn"
                value="area"
                style={{
                  background:
                    value === "area"
                      ? theme === "light"
                        ? "linear-gradient(rgb(196, 53, 200) 34.17%, rgb(94, 104, 240) 100%)"
                        : "#DBE3F8"
                      : "transparent",
                  color:
                    value === "area"
                      ? theme === "light"
                        ? "#fff"
                        : "#000"
                      : "",
                }}
              >
                Area Code
              </Radio.Button>
              <Radio.Button
                className="tab-btn"
                value="city"
                style={{
                  background:
                    value === "city"
                      ? theme === "light"
                        ? "linear-gradient(rgb(196, 53, 200) 34.17%, rgb(94, 104, 240) 100%)"
                        : "#DBE3F8"
                      : "transparent",
                  color:
                    value === "city"
                      ? theme === "light"
                        ? "#fff"
                        : "#000"
                      : "",
                }}
              >
                City Name
              </Radio.Button>
            </Radio.Group>
          </div>

          <div className="section">
            {value === "city" ? (
              <>
                <Card
                  className="country-selector"
                  onClick={() => {
                    if (isLoading) return;
                    setModalVisible(true);
                  }}
                  bordered={false}
                  styles={{
                    body: {
                      padding: "8px 16px",
                    },
                  }}
                >
                  <Flex align="center" gap={8}>
                    <img
                      src={selectedCountry?.flag}
                      alt="US flag"
                      style={{
                        width: "28px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                    <span className="country-code">
                      {selectedCountry?.dial}
                    </span>
                    <span className="country-name">
                      {selectedCountry?.name}
                    </span>
                  </Flex>
                </Card>

                <Card
                  className="state-selector"
                  styles={{
                    body: {
                      padding: "8px 16px",
                      display: "flex",
                      flexDirection: "row",
                    },
                  }}
                  bordered={false}
                  onClick={() => {
                    if (isLoading) return;
                    setStateSelectorModalVisible(true);
                  }}
                >
                  <EnvironmentFilled
                    className="text-primary"
                    style={{ fontSize: "18px" }}
                  />
                  <Input
                    readOnly
                    value={(selectedState && selectedState.value) || ""}
                    placeholder="Select State"
                    className="state-input"
                  />
                </Card>

                <Card
                  className="city-selector"
                  bordered={false}
                  styles={{
                    body: {
                      padding: "8px 16px",
                      display: "flex",
                      flexDirection: "row",
                    },
                  }}
                  onClick={() => {
                    if (isLoading) return;
                    setCitySelectorModalVisible(true);
                  }}
                >
                  <EnvironmentFilled
                    className="text-primary"
                    style={{ fontSize: "18px" }}
                  />
                  <Input
                    readOnly
                    value={(selectedCity && selectedCity.value) || ""}
                    placeholder="Select City"
                    className="city-input-field"
                  />
                </Card>

                <Card
                  className="phone-number-input"
                  styles={{
                    body: {
                      padding: "8px 16px",
                    },
                  }}
                  bordered={false}
                >
                  <Flex align="center" gap={8}>
                    <img
                      src={selectedCountry?.flag}
                      alt="US flag"
                      style={{
                        width: "28px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                    <span className="country-code">
                      {selectedCountry?.dial}
                    </span>
                    <span className="country-name">
                      {selectedNumber || "xxx-xxx-xxxx"}
                    </span>
                  </Flex>
                </Card>
              </>
            ) : (
              <>
                <Card
                  className="country-selector"
                  styles={{
                    body: {
                      padding: "8px 16px",
                    },
                  }}
                  bordered={false}
                >
                  <Flex
                    align="center"
                    gap={8}
                    onClick={() => {
                      if (isLoading) return;
                      setModalVisible(true);
                    }}
                  >
                    <img
                      src={selectedCountry?.flag}
                      alt="US flag"
                      style={{
                        width: "28px",
                        height: "16px",
                        marginRight: "8px",
                      }}
                    />
                    <span className="country-code">
                      {selectedCountry?.dial}
                    </span>
                    <span className="country-name">
                      {selectedCountry?.name}
                    </span>
                  </Flex>
                  {/* <CountryActionMenus /> */}
                </Card>
                <Card
                  className="area-code-input"
                  bordered={false}
                  styles={{
                    body: {
                      padding: "8px 16px",
                    },
                  }}
                >
                  <Form>
                    <Flex className="w-full" align="center" gap={10}>
                      <EnvironmentFilled
                        className="text-primary"
                        style={{ fontSize: "16px" }}
                      />
                      <Input
                        onChange={(e) => {
                          const onlyNumbers = e.target.value.replace(/\D/g, "");
                          setAreaCodeSearchQuery(onlyNumbers);
                        }}
                        placeholder="Search area code"
                        className="area-input-field"
                      />
                      <GradientButton
                        htmlType="submit"
                        className="search-btn"
                        disabled={isLoading}
                        onClick={getNumbersFromNPA}
                      >
                        Search
                      </GradientButton>
                    </Flex>
                  </Form>
                </Card>
              </>
            )}

            {isLoading ? (
              <div className="loader-container">
                <CustomLoader />
              </div>
            ) : carouselChunks.length > 0 ? (
              <div className="fetched-numbers-container">
                <div className="selected-number">{selectedNumber}</div>
                <div
                  onClick={() => carouselRef.current?.prev()}
                  className="custom-left-arrow"
                >
                  <LeftOutlined />
                </div>
                <Carousel dots={false} ref={carouselRef}>
                  {carouselChunks.map((group, index) => (
                    <div key={index} className="fetched-numbers-slide">
                      <Radio.Group
                        onChange={(e) => setSelectedNumber(e.target.value)}
                      >
                        <div className="radio-grid">
                          {group.map((number: any) => (
                            <Radio
                              className="custom-radio"
                              value={number?.id}
                              key={number.id}
                            >
                              {number?.didSummary}
                            </Radio>
                          ))}
                        </div>
                      </Radio.Group>
                    </div>
                  ))}
                </Carousel>
                <div
                  onClick={() => carouselRef.current?.next()}
                  className="custom-right-arrow"
                >
                  <RightOutlined />
                </div>
              </div>
            ) : null}
          </div>
        </div>

        <div className="buy-now-btn-container">
          <GradientButton onClick={handleBuying}>Buy Now</GradientButton>
        </div>
      </div>

      <Modal
        open={paymentGatewayModalOpen}
        onCancel={() => setPaymentGatewayModalOpen(false)}
        footer={null}
      >
        <iframe
          title="Payment Gateway"
          src={paymentGatewayDetails?.url}
          width="100%"
          height="660px"
          style={{ border: "none" }}
          allowFullScreen
        />
      </Modal>

      <Modal
        centered
        className="custom-modal"
        title="Confirm Purchase"
        open={isConfirmationModalOpen}
        onOk={handleBuyingConfirmation}
        onCancel={() => setConfirmationModalOpen(false)}
        okText="Yes"
        cancelText="No"
        cancelButtonProps={{
          className: "modal-cancel-btn",
          type: "text",
          disabled: buyingInProgress,
          style: {
            height: "40px",
          },
        }}
        okButtonProps={{
          className: "modal-ok-btn",
          loading: buyingInProgress,
          style: {
            height: "40px",
          },
        }}
      >
        <p>
          Are you sure you want to buy {selectedCountry?.dial}
          {phoneNumberDetails?.phone} for {phoneNumberDetails?.cost}$ monthly?
        </p>
      </Modal>

      <CountrySelectorModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSelect={(country) => {
          setSelectedCountry(country);
          setModalVisible(false);
        }}
      />

      <StateSelectorModal
        visible={stateSelectorModalVisible}
        onClose={() => setStateSelectorModalVisible(false)}
        country={selectedCountry?.code}
        onSelect={(state: any) => {
          setSelectedState(state);
          setStateSelectorModalVisible(false);
        }}
      />

      <CitySelectorModal
        visible={citySelectorModalVisible}
        onClose={() => setCitySelectorModalVisible(false)}
        state={selectedState?.id}
        onSelect={(state: any) => {
          setSelectedCity(state);
          setCitySelectorModalVisible(false);
        }}
      />
    </>
  );
};

export default BuyNumber;
